import { useState } from "react";

import { ChevronDown, Download, Eye, FileText, Sparkles } from "lucide-react";

import { Button } from "@components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@components/ui/popover";

type Props = {
  onPreview: () => void;
  onToggleQA: () => void;
  onDownloadDocx: () => void;
  onDownloadPdf: () => void;
  isDownloading: boolean;
  isDownloadingPdf: boolean;
  /** PDF needs a saved draft response; disabled until one exists. */
  canDownloadPdf: boolean;
};

export const HeaderActions = ({
  onPreview,
  onToggleQA,
  onDownloadDocx,
  onDownloadPdf,
  isDownloading,
  isDownloadingPdf,
  canDownloadPdf,
}: Props) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const busy = isDownloading || isDownloadingPdf;

  return (
    <div className="flex items-center gap-3">
      <Button variant="simple-outline" size="sm" onClick={onPreview}>
        <Eye className="mr-1.5 size-4" />
        Preview
      </Button>

      {/* Report QA — accent (purple) brand, faint tinted bg */}
      <Button variant="accent-subtle" size="sm" onClick={onToggleQA}>
        <Sparkles className="mr-1.5 size-4" />
        Report QA
      </Button>

      {/* Split button: primary = Download PDF Report; caret opens .docx option. */}
      <div className="flex">
        <Button
          size="sm"
          className="rounded-r-none"
          onClick={onDownloadPdf}
          disabled={busy || !canDownloadPdf}
          isLoading={isDownloadingPdf}
          title={
            canDownloadPdf
              ? "Download PDF report"
              : "Save a draft first to export PDF"
          }
        >
          <Download className="mr-1.5 size-4" />
          Download PDF Report
        </Button>
        <Popover open={menuOpen} onOpenChange={setMenuOpen}>
          <PopoverTrigger asChild>
            <Button
              size="sm"
              className="rounded-l-none border-l border-l-white/20 px-2"
              disabled={busy}
              aria-label="More download options"
            >
              <ChevronDown className="size-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-48 p-1">
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                onDownloadDocx();
              }}
              disabled={isDownloading}
              className="hover:bg-muted flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm disabled:opacity-50"
            >
              <FileText className="size-4" />
              Download .docx
            </button>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};
